import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { aiCmaAPI, appointmentAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { 
  Robot, 
  Microphone, 
  MicrophoneSlash,
  PaperPlaneTilt, 
  CheckCircle,
  SpinnerGap,
  SpeakerHigh,
  SpeakerSlash,
  ArrowRight,
  FirstAidKit,
  Heart,
  Pill,
  Warning,
  ChatCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PatientAICMAPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [upcomingAppointment, setUpcomingAppointment] = useState(null);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check config and load session on mount
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Check if AI CMA is configured
      const configRes = await aiCmaAPI.getConfig();
      setConfig(configRes.data);
      
      // Get upcoming appointments
      const apptRes = await appointmentAPI.getAll();
      // Find upcoming scheduled/confirmed appointment
      const appointments = apptRes.data?.appointments || apptRes.data || [];
      const upcoming = appointments.find(
        a => a.status === 'scheduled' || a.status === 'confirmed'
      );
      setUpcomingAppointment(upcoming);
      
      // Check for existing session
      const sessionRes = await aiCmaAPI.getSession(upcoming?.id);
      if (sessionRes.data.session) {
        setSession(sessionRes.data.session);
        // Restore conversation history
        if (sessionRes.data.session.conversation_history?.length > 0) {
          setMessages(sessionRes.data.session.conversation_history.map(msg => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp
          })));
        }
        if (sessionRes.data.session.status === 'completed') {
          setSessionCompleted(true);
        }
      }
    } catch (error) {
      console.error('Error loading AI CMA data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    try {
      setSending(true);
      const res = await aiCmaAPI.startSession(upcomingAppointment?.id);
      setSession(res.data);
      setMessages([{
        role: 'assistant',
        content: res.data.greeting,
        timestamp: new Date().toISOString()
      }]);
      
      // Play greeting if voice enabled
      if (voiceEnabled && config?.features?.voice_output) {
        speakText(res.data.greeting);
      }
    } catch (error) {
      toast.error('Failed to start session. Please try again.');
      console.error('Error starting session:', error);
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    try {
      setSending(true);
      const res = await aiCmaAPI.chat({
        message: userMessage,
        appointment_id: upcomingAppointment?.id
      });
      
      // Add AI response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date().toISOString()
      }]);
      
      // Update session with extracted data
      if (res.data.extracted_data) {
        setSession(prev => ({
          ...prev,
          vitals: { ...prev?.vitals, ...res.data.extracted_data }
        }));
      }
      
      // Play response if voice enabled
      if (voiceEnabled && config?.features?.voice_output) {
        speakText(res.data.response);
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processVoiceInput(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      toast.error('Could not access microphone. Please check permissions.');
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processVoiceInput = async (audioBlob) => {
    try {
      setIsTranscribing(true);
      
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          const res = await aiCmaAPI.voiceInput({
            audio_base64: base64Audio,
            appointment_id: upcomingAppointment?.id
          });
          
          // Add transcribed user message
          if (res.data.transcription) {
            setMessages(prev => [...prev, {
              role: 'user',
              content: res.data.transcription,
              timestamp: new Date().toISOString(),
              isVoice: true
            }]);
          }
          
          // Add AI response
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: res.data.response,
            timestamp: new Date().toISOString()
          }]);
          
          // Play audio response if available
          if (res.data.audio_base64 && voiceEnabled) {
            playAudioResponse(res.data.audio_base64);
          }
          
          // Update session with extracted data
          if (res.data.extracted_data) {
            setSession(prev => ({
              ...prev,
              vitals: { ...prev?.vitals, ...res.data.extracted_data }
            }));
          }
        } catch (error) {
          toast.error('Failed to process voice input. Please try again.');
          console.error('Error processing voice:', error);
        } finally {
          setIsTranscribing(false);
        }
      };
    } catch (error) {
      toast.error('Error processing audio.');
      setIsTranscribing(false);
    }
  };

  const speakText = async (text) => {
    try {
      setIsSpeaking(true);
      const res = await aiCmaAPI.textToSpeech(text);
      
      // Create audio from blob
      const audioBlob = new Blob([res.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => setIsSpeaking(false);
        await audioRef.current.play();
      }
    } catch (error) {
      console.error('Error playing speech:', error);
      setIsSpeaking(false);
    }
  };

  const playAudioResponse = (base64Audio) => {
    try {
      setIsSpeaking(true);
      const audioBytes = atob(base64Audio);
      const audioArray = new Uint8Array(audioBytes.length);
      for (let i = 0; i < audioBytes.length; i++) {
        audioArray[i] = audioBytes.charCodeAt(i);
      }
      const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => setIsSpeaking(false);
        audioRef.current.play();
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsSpeaking(false);
    }
  };

  const completeSession = async () => {
    try {
      setSending(true);
      await aiCmaAPI.completeSession(session?.id);
      setSessionCompleted(true);
      toast.success('Thank you! Your care team will review your answers before your appointment.');
    } catch (error) {
      toast.error('Failed to complete session. Please try again.');
      console.error('Error completing session:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <SpinnerGap size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  if (!config?.configured) {
    return (
      <div className="p-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <Robot size={64} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">AI Assistant Not Available</h2>
            <p className="text-slate-500">The Virtual Medical Assistant is currently unavailable. Please contact the clinic for assistance.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Hidden audio element for playback */}
      <audio ref={audioRef} className="hidden" />
      
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-teal-100 rounded-lg">
            <Robot size={28} weight="duotone" className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Virtual Medical Assistant
            </h1>
            <p className="text-slate-500 text-sm">Prepare for your appointment by answering a few questions</p>
          </div>
        </div>
        
        {upcomingAppointment && (
          <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-100">
            <p className="text-sm text-teal-700">
              <span className="font-medium">Upcoming Appointment:</span>{' '}
              {new Date(upcomingAppointment.scheduled_time).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          </div>
        )}
      </div>

      {/* Info Cards - What we collect */}
      {!session && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="py-4 text-center">
              <Heart size={24} className="mx-auto text-red-500 mb-2" />
              <p className="text-xs text-slate-600 font-medium">Vitals</p>
              <p className="text-xs text-slate-400">BP, Pulse, Temp</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="py-4 text-center">
              <FirstAidKit size={24} className="mx-auto text-blue-500 mb-2" />
              <p className="text-xs text-slate-600 font-medium">Chief Complaint</p>
              <p className="text-xs text-slate-400">Reason for Visit</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="py-4 text-center">
              <Warning size={24} className="mx-auto text-amber-500 mb-2" />
              <p className="text-xs text-slate-600 font-medium">Symptoms</p>
              <p className="text-xs text-slate-400">Current Issues</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="py-4 text-center">
              <Pill size={24} className="mx-auto text-purple-500 mb-2" />
              <p className="text-xs text-slate-600 font-medium">Medications</p>
              <p className="text-xs text-slate-400">Updates & Allergies</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Session Not Started */}
      {!session && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ready to Get Started?</CardTitle>
            <CardDescription>
              Our Virtual Medical Assistant will help collect your health information before your appointment. 
              You can type or speak your responses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle size={18} className="text-green-500" />
                <span>Takes about 5-10 minutes</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle size={18} className="text-green-500" />
                <span>Your answers help your provider prepare</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle size={18} className="text-green-500" />
                <span>Completely optional - not required for your visit</span>
              </div>
              
              <Button
                onClick={startSession}
                disabled={sending}
                className="w-full mt-4 bg-teal-600 hover:bg-teal-700"
                data-testid="start-ai-cma-btn"
              >
                {sending ? (
                  <SpinnerGap size={20} className="animate-spin mr-2" />
                ) : (
                  <ChatCircle size={20} className="mr-2" />
                )}
                Start Virtual Check-in
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Session */}
      {session && !sessionCompleted && (
        <Card className="flex flex-col h-[600px]">
          <CardHeader className="border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Robot size={24} className="text-teal-600" />
                <CardTitle className="text-lg">Virtual Medical Assistant</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setVoiceEnabled(!voiceEnabled)}
                  title={voiceEnabled ? 'Disable voice' : 'Enable voice'}
                >
                  {voiceEnabled ? (
                    <SpeakerHigh size={20} className="text-teal-600" />
                  ) : (
                    <SpeakerSlash size={20} className="text-slate-400" />
                  )}
                </Button>
                {isSpeaking && (
                  <span className="text-xs text-teal-600 animate-pulse">Speaking...</span>
                )}
              </div>
            </div>
          </CardHeader>
          
          {/* Messages */}
          <CardContent className="flex-1 overflow-y-auto py-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={`msg-${msg.timestamp}-${index}`}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-100 text-slate-800'
                  }`}
                >
                  {msg.isVoice && (
                    <Microphone size={14} className="inline mr-1 opacity-70" />
                  )}
                  {msg.content}
                </div>
              </div>
            ))}
            {(sending || isTranscribing) && (
              <div className="flex justify-start">
                <div className="bg-slate-100 p-3 rounded-lg">
                  <SpinnerGap size={20} className="animate-spin text-teal-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>
          
          {/* Input Area */}
          <div className="border-t p-4 flex-shrink-0">
            <div className="flex gap-2">
              {config?.features?.voice_input && (
                <Button
                  variant={isRecording ? 'destructive' : 'outline'}
                  size="icon"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={sending || isTranscribing}
                  title={isRecording ? 'Stop recording' : 'Start voice input'}
                  data-testid="voice-input-btn"
                >
                  {isRecording ? (
                    <MicrophoneSlash size={20} />
                  ) : isTranscribing ? (
                    <SpinnerGap size={20} className="animate-spin" />
                  ) : (
                    <Microphone size={20} />
                  )}
                </Button>
              )}
              
              <Textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your response..."
                className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                disabled={sending || isRecording}
                data-testid="chat-input"
              />
              
              <Button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || sending}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="send-message-btn"
              >
                <PaperPlaneTilt size={20} />
              </Button>
            </div>
            
            {isRecording && (
              <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                Recording... Click the microphone to stop
              </div>
            )}
            
            {/* Complete Session Button */}
            {messages.length >= 4 && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  onClick={completeSession}
                  disabled={sending}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="complete-session-btn"
                >
                  <CheckCircle size={20} className="mr-2" />
                  Complete Pre-Visit Check-in
                </Button>
                <p className="text-xs text-slate-500 text-center mt-2">
                  Click when you've shared all your information
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Session Completed */}
      {sessionCompleted && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-12 text-center">
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-semibold text-green-800 mb-2">Pre-Visit Check-in Complete!</h2>
            <p className="text-green-700 mb-4">
              Thank you for providing your health information. Your provider will review it before your appointment.
            </p>
            <div className="text-sm text-green-600">
              {upcomingAppointment && (
                <p>
                  See you on{' '}
                  {new Date(upcomingAppointment.scheduled_time).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
            
            <Button
              variant="outline"
              className="mt-6"
              onClick={() => window.location.href = '/patient/appointments'}
            >
              <ArrowRight size={18} className="mr-2" />
              Back to Appointments
            </Button>
            <Button
              variant="ghost"
              className="mt-6 ml-2"
              disabled={sending}
              onClick={() => { setSessionCompleted(false); setMessages([]); startSession(); }}
              data-testid="start-new-checkin-btn"
            >
              Start a new check-in
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
